import { isEmpty } from 'lodash'
import { useCallback } from 'react'
import { useRecoilState } from 'recoil'

import { itemSelector } from '@/atoms/_editorAtoms'

type ImageEdits = {
  targetSize?: {
    width: number
    height: number
  }
  [key: string]: unknown
}

/**
 * The whole pixel an item's size is held at: the atoms, and the rows behind
 * them, keep integers. The commit rounds with this, and so does a text box's
 * fit while a pill is held (`PageEditorTextBox`), so the frame the fit
 * measures under the pointer is the frame the release writes; a fractional
 * frame there would paint one size under the pointer and another on release
 * (docs/lotus/sizing.md, "Handles").
 */
export const roundItemSizePx = (value: number): number => Math.round(value)

/**
 * Hook for resizing items (width/height) with shared logic.
 *
 * This centralizes resize logic used by:
 * - ImageDrawer (sidebar Size inputs)
 * - ResizeControls and ClassicResizeControls (corner drag)
 * - ClassicEdgeResizeControls (a classic image's edge drag)
 *
 * @param itemId - The unique ID of the item to resize
 */
export const useItemResize = (itemId: string) => {
  const [widthRaw, setWidth] = useRecoilState(
    itemSelector({ id: itemId, path: 'width' }),
  )
  const [heightRaw, setHeight] = useRecoilState(
    itemSelector({ id: itemId, path: 'height' }),
  )
  const [imageEdits, setImageEdits] = useRecoilState<ImageEdits>(
    itemSelector({ id: itemId, path: 'image.edits' }),
  )

  // Ensure we always return numbers (default to 0 if undefined)
  const width = typeof widthRaw === 'number' ? widthRaw : 0
  const height = typeof heightRaw === 'number' ? heightRaw : 0

  /**
   * Updates image.edits.targetSize to match new dimensions.
   * This is needed for the image rendering to know the container size.
   */
  const updateImageEditsTargetSize = useCallback(
    (newWidth: number, newHeight: number) => {
      if (isEmpty(imageEdits)) return

      setImageEdits({
        ...imageEdits,
        targetSize: {
          width: newWidth,
          height: newHeight,
        },
      })
    },
    [imageEdits, setImageEdits],
  )

  /**
   * Set width only (for right edge handle or Width input).
   * Also updates imageEdits.targetSize.width.
   */
  const setWidthSize = useCallback(
    (value: number) => {
      if (!value) return
      const roundedValue = roundItemSizePx(value)
      setWidth(roundedValue)

      if (!isEmpty(imageEdits)) {
        setImageEdits({
          ...imageEdits,
          targetSize: {
            width: roundedValue,
            height: imageEdits.targetSize?.height ?? height,
          },
        })
      }
    },
    [setWidth, imageEdits, setImageEdits, height],
  )

  /**
   * Set height only (for bottom edge handle or Height input).
   * Also updates imageEdits.targetSize.height.
   */
  const setHeightSize = useCallback(
    (value: number) => {
      if (!value) return
      const roundedValue = roundItemSizePx(value)
      setHeight(roundedValue)

      if (!isEmpty(imageEdits)) {
        setImageEdits({
          ...imageEdits,
          targetSize: {
            width: imageEdits.targetSize?.width ?? width,
            height: roundedValue,
          },
        })
      }
    },
    [setHeight, imageEdits, setImageEdits, width],
  )

  /**
   * Set both width and height together (for corner resize).
   * Also updates imageEdits.targetSize.
   */
  const setSize = useCallback(
    (newWidth: number, newHeight: number) => {
      if (!newWidth || !newHeight) return
      const roundedWidth = roundItemSizePx(newWidth)
      const roundedHeight = roundItemSizePx(newHeight)

      setWidth(roundedWidth)
      setHeight(roundedHeight)
      updateImageEditsTargetSize(roundedWidth, roundedHeight)
    },
    [setWidth, setHeight, updateImageEditsTargetSize],
  )

  return {
    width,
    height,
    setWidthSize,
    setHeightSize,
    setSize,
  }
}
